import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, map, Observable, of, ReplaySubject, shareReplay, tap } from 'rxjs';
import { AgentsListApi } from 'app/core/chat/agents-list.service';
import { AgentListQuery, AgentListResponse, AgentOrderMove, AgentOrderResult } from './agent-list.types';
import { Agent, AgentDelegationLink, AgentOwnerCandidate, AgentPosition, AgentShareCandidate, AgentTreeSettings, AgentTreeSettingsPatch, SubAgentRef } from './agent';
import { NumberFormat } from 'xlsx';

export const reportingFormats: any[] = [
  {
    id: 1,
    name: 'HTML'
  },
  {
    id: 2,
    name: 'Email'
  },
  {
    id: 3,
    name: 'PDF'
  }
]

export const detailLevels: any[] = [
  {
    id: 2,
    name: 'High Level Of Detail'
  },
  {
    id: 1,
    name: 'Normal Level Of Detail'
  },

]

@Injectable({ providedIn: 'root' })
export class AgentService {
  private apiLink = environment.apiLink
  private _httpClient = inject(HttpClient);
  /** Shared with the messenger app (`core/chat`): one client for `GET agents/agents`. */
  private _agentsList = inject(AgentsListApi);

  private selectedAgentId: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  /** Per-session cache for `getSubAgents` — one in-flight/settled request per agent eid. */
  private subAgentsByAgentId = new Map<string, Observable<SubAgentRef[]>>();

  setSelectedAgentId(id: string) {
    this.selectedAgentId.next(id);
  }

  get selectedAgentId$(): Observable<string | null> {
    return this.selectedAgentId.asObservable();
  }

  currentSelectedAgentId(): string | null {
    return this.selectedAgentId.value;
  }

  constructor() { }

  getAll(name?: string): Observable<Agent[]> {
    return this._agentsList.getAll(name);
  }

  getById(id: string): Observable<Agent> {
    return this._httpClient.get<Agent>(this.apiLink + `agents/${id}/agent`).pipe(
      map((res: any) => {
        return res.data.agent
      }),
    );
  }

  /**
   * Agents eligible to be configured as sub-agents (delegates) for the given agent:
   * the agents the current user can access (company user: all owned agents; member/staff:
   * own + shared agents), optionally narrowed to one or more departments. Access scoping is
   * enforced server-side.
   * Backend: GET agents/:agentId/sub-agent-candidates (see backend PATTERNS.md §4.2.2).
   */
  getSubAgentCandidates(agentId: string, inDepartment?: number[]): Observable<SubAgentRef[]> {
    let params = new HttpParams()
    for (const departmentId of inDepartment ?? []) {
      params = params.append('inDepartment', departmentId)
    }
    return this._httpClient
      .get<SubAgentRef[]>(this.apiLink + `agents/${agentId}/sub-agent-candidates`, { params })
      .pipe(map((res: any) => res.data.agents as SubAgentRef[]));
  }

  /**
   * The sub-agents this agent actually delegates to — the names behind the list's `subAgentsCount`.
   *
   * Read off `GET agents/:id/agent`, which is the only endpoint that reports the configured
   * delegates. **Not `getSubAgentCandidates`**: that is the editor's picker (every agent in the
   * account that *could* be chosen, never the `agent_sub_agents` join), so it would answer a count
   * of 3 with the whole org, and it requires edit permission, which 403s for a viewer the count
   * rendered fine for.
   *
   * The detail response is heavy (data sources, tables, uploads, departments, members, staff,
   * instructions), so the result is cached per agent for the session: the popover is opened and
   * closed repeatedly and only ever reads this one field. A failed request evicts itself so Retry
   * re-issues rather than replaying the error. Call `invalidateSubAgents` after anything that can
   * change the delegation list (agent edit, delete).
   */
  getSubAgents(agentId: string): Observable<SubAgentRef[]> {
    const cached = this.subAgentsByAgentId.get(agentId);
    if (cached) {
      return cached;
    }

    const request = this.getById(agentId).pipe(
      map((agent: Agent) => agent?.subAgents ?? []),
      tap({ error: () => this.subAgentsByAgentId.delete(agentId) }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.subAgentsByAgentId.set(agentId, request);
    return request;
  }

  /** Drops the cached sub-agent list for one agent, or for all of them when no id is given. */
  invalidateSubAgents(agentId?: string): void {
    if (agentId) {
      this.subAgentsByAgentId.delete(agentId);
      return;
    }
    this.subAgentsByAgentId.clear();
  }

  getAgentsWithSettings(id: string) {
    return this._httpClient.get<Agent>(this.apiLink + `agents/${id}/agent-with-settings`).pipe(
      map((res: any) => {
        return res.data.agent
      }),
    );
  }

  create(modal: any) {
    return this._httpClient.post(this.apiLink + 'agents/new-agent', modal)
  }

  update(modal: any, id: string) {
    return this._httpClient.put(this.apiLink + `agents/${id}/edit-agent`, modal)
  }

  updateSettings(modal: any, id: string) {
    return this._httpClient.put(this.apiLink + `agents/${id}/edit-agent-settings`, modal)
  }

  updateStatus(id: string, active: boolean) {
    const params = new HttpParams().append('active', active)
    return this._httpClient.put(this.apiLink + `agents/${id}/change-status`, {}, { params })
  }

  delete(id: string) {
    return this._httpClient.delete(this.apiLink + `agents/${id}/delete-agent`)
  }

  /**
   * Save the signed-in user's own card order. `agentIds` is the FULL order, first card first: the
   * backend replaces the saved order with it, so an agent left out loses its place. `[]` clears it.
   * The new order is also pushed on `WS agents/get-agents`.
   *
   * Backend: PUT agents/reorder-agents (400 on a duplicate eid, 404 on one the user cannot see).
   */
  reorderAgents(agentIds: string[]): Observable<AgentPosition[]> {
    return this._httpClient
      .put<{ data?: { agents?: AgentPosition[] } }>(this.apiLink + 'agents/reorder-agents', { agentIds })
      .pipe(map(res => res.data?.agents ?? []));
  }

  /**
   * One page of the agents the signed-in user may view, as the Agents page renders them, in the
   * user's personal order, with the total that matches `search` and `departmentId`. Each card
   * carries `updatedAt` for comparison with SSE events. `query.all` is not sent: the caller pages.
   *
   * Backend: GET agents/list (backend ADR 0019). The body may arrive wrapped in `{ data }`.
   */
  getList(query: Pick<AgentListQuery, 'search' | 'departmentId' | 'offset' | 'limit'>): Observable<AgentListResponse> {
    let params = new HttpParams().set('limit', query.limit).set('offset', query.offset);
    const search = query.search.trim();
    if (search) {
      params = params.set('search', search);
    }
    if (query.departmentId != null) {
      params = params.set('departmentId', query.departmentId);
    }
    return this._httpClient
      .get<{ data?: AgentListResponse } & Partial<AgentListResponse>>(this.apiLink + 'agents/list', { params })
      .pipe(
        map((res) => {
          const body = (res?.data ?? res) as Partial<AgentListResponse>;
          return {
            agents: body.agents ?? [],
            total: body.total ?? 0,
            limit: body.limit ?? query.limit,
            offset: body.offset ?? query.offset,
          };
        }),
      );
  }

  /**
   * Move one card next to another on the page the user is looking at. The server owns the full
   * order, so agents outside the page keep their relative order. Exactly one of `afterEid` and
   * `beforeEid`.
   *
   * Backend: PUT agents/list/order (400 on a bad target, 404 on an eid the user cannot see).
   */
  reorderList(move: AgentOrderMove): Observable<AgentOrderResult> {
    return this._httpClient
      .put<{ data?: AgentOrderResult } & Partial<AgentOrderResult>>(this.apiLink + 'agents/list/order', move)
      .pipe(map(res => (res?.data ?? res) as AgentOrderResult));
  }

  /**
   * Saved names and related nodes of every Agent Tree whose root the user can see. A root missing
   * from the answer has never been customised: draw it with its agent's name and no related nodes.
   *
   * Backend: GET agents/trees.
   */
  getAgentTrees(): Observable<AgentTreeSettings[]> {
    return this._httpClient
      .get<{ data?: { trees?: AgentTreeSettings[] } }>(this.apiLink + 'agents/trees')
      .pipe(map(res => res.data?.trees ?? []));
  }

  /**
   * Name a tree and/or replace its related nodes. A field left out is left alone; `name: null`
   * goes back to the root agent's name. Only someone who can edit the root agent may call it (403
   * otherwise). Nodes naming an agent the organization no longer has are dropped, so the answer is
   * what was actually saved.
   *
   * Backend: PUT agents/trees/:rootAgentId.
   */
  saveAgentTree(rootAgentEid: string, patch: AgentTreeSettingsPatch): Observable<AgentTreeSettings> {
    return this._httpClient
      .put<{ data: AgentTreeSettings }>(this.apiLink + `agents/trees/${encodeURIComponent(rootAgentEid)}`, patch)
      .pipe(map(res => res.data));
  }

  /**
   * Replace the agents `agentId` delegates to with `subAgentEids` (the whole list, at most 1000),
   * leaving every other field of the agent alone. Used by the Tree View editor. Answers the list as
   * saved, which can be shorter than the one sent (the access policy may drop a link at once).
   *
   * Backend: PUT agents/:agentId/sub-agents `{ subAgents: string[] }`. 400 bad body or self-link,
   * 403 may see but not edit, 404 parent or a sub-agent unknown / not accessible, 409 a delegation
   * cycle OR another sub-agent save in the organization still running. Show the server `message`.
   */
  setSubAgents(agentId: string, subAgentEids: string[]): Observable<SubAgentRef[]> {
    return this._httpClient
      .put<{ data?: { agent?: { subAgents?: SubAgentRef[] } } }>(this.apiLink + `agents/${encodeURIComponent(agentId)}/sub-agents`, { subAgents: subAgentEids })
      .pipe(map(res => res.data?.agent?.subAgents ?? []));
  }

  /**
   * Every delegation link whose parent is in the caller's agents list (the same visibility as
   * `WS agents/get-agents`), in ONE call: the Tree View builds its whole parent -> children map from
   * it. A child the caller cannot see comes back with `accessible: false` and no name. A shared
   * delegate appears under each parent. Not cached server-side.
   *
   * Backend: GET agents/delegations.
   */
  getDelegations(): Observable<AgentDelegationLink[]> {
    return this._httpClient
      .get<{ data?: { links?: AgentDelegationLink[] } }>(this.apiLink + 'agents/delegations')
      .pipe(map(res => res.data?.links ?? []));
  }

  /** Forget a tree's name and related nodes. Backend: DELETE agents/trees/:rootAgentId. */
  resetAgentTree(rootAgentEid: string): Observable<AgentTreeSettings> {
    return this._httpClient
      .delete<{ data: AgentTreeSettings }>(this.apiLink + `agents/trees/${encodeURIComponent(rootAgentEid)}`)
      .pipe(map(res => res.data));
  }

  /**
   * People who may be made the owner of an agent built on these data sources. The server applies
   * both the eligibility rule and the "who may you hand it to" rule, so anything returned is a
   * valid choice. See backend `docs/ACCESS_CONTROL_POLICY.md` §4 and §8.1.
   *
   * Backend: GET agents/owner-candidates.
   */
  getOwnerCandidates(dataSourceEids: string[]): Observable<AgentOwnerCandidate[]> {
    let params = new HttpParams();
    for (const eid of dataSourceEids ?? []) {
      params = params.append('dataSources', eid);
    }
    return this._httpClient
      .get(this.apiLink + 'agents/owner-candidates', { params })
      .pipe(map((res: any) => (res.data?.candidates ?? []) as AgentOwnerCandidate[]));
  }

  /**
   * People this agent can be shared with, each annotated with how much of the agent's data they
   * can actually see. Backend enforces that only the owner and the admin may list them.
   *
   * Coverage is disclosure, not a gate: sharing with a partially covered person is allowed, and the
   * backend narrows their runs to what they can see. See backend
   * `docs/ACCESS_CONTROL_POLICY.md` §9.3.
   *
   * Backend: GET agents/:agentId/share-candidates.
   */
  getShareCandidates(agentId: string): Observable<AgentShareCandidate[]> {
    return this._httpClient
      .get(this.apiLink + `agents/${agentId}/share-candidates`)
      .pipe(map((res: any) => (res.data?.candidates ?? []) as AgentShareCandidate[]));
  }

  shareAgent(body: {
    agentId: string;
    memberIds: number[];
    staffIds: number[];
    userIds: number[];
    departmentIds: number[];
  }) {
    return this._httpClient.put(this.apiLink + 'agents/share-agent', body);
  }

  getQuestions(agentId: string, page: number, size: number) {
    let params = new HttpParams()
    params = params.append('agentId', agentId)
    params = params.append('limit', size)
    params = params.append('offset', page)
    return this._httpClient.get(this.apiLink + 'questionaire/get-questionnaire', { params }).pipe(
      map((res: any) => {
        return { data: res.data.questionnare, count: res.data.count }
      }),
    );
  }
  generateQuestions(modal: any) {
    return this._httpClient.post(this.apiLink + 'questionaire/generate', modal)
  }

  addNewQuestion(modal) {
    return this._httpClient.post(this.apiLink + 'questionaire/new-questions', modal)
  }

  executeQuestions(modal) {
    return this._httpClient.post(this.apiLink + 'questionaire/execute', modal)
  }

  stopQuestionnaireExecution(agentId: string) {
    return this._httpClient.post(this.apiLink + 'questionaire/stop-execution', { agentId })
  }

  deleteAllQuestions(agentId: string) {
    return this._httpClient.delete(this.apiLink + `questionaire/delete-question/${agentId}`)
  }

  deleteQuestion(questionId: number, agentId: string) {
    return this._httpClient.delete(this.apiLink + `questionaire/delete-question/${questionId}/${agentId}`)
  }


  generateQrCode(name): Promise<string> {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const size = 500; // Size of the image
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      // Draw rounded image
      const img = new Image();
      img.src = '../../../../../../../assets/images/agent1.jpg'; // Path to the image
      img.crossOrigin = 'anonymous'; // To handle cross-origin images

      img.onload = () => {
        // Draw rounded rectangle for the image
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.clip();

        ctx.drawImage(img, 0, 0, size, size);

        // Draw text in the center
        ctx.font = 'bold 100px Arial';
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(name, size / 2, size / 2);

        // Resolve the promise with the image data URL
        resolve(canvas.toDataURL());
      };

      img.onerror = () => {
        // In case of an error, resolve with a fallback (blank) data URL
        resolve(canvas.toDataURL());
      };
    });
  }

      generateAgentDescription(id: any) {
    return this._httpClient.get(this.apiLink + 'agents/generate-agent-description/' +  id );
}

}

